import React, { useState } from 'react';
import { Share2, Copy, Check, Clock, ExternalLink, X, Mail, MessageCircle } from 'lucide-react';
import { useShareAnalysis } from '../hooks/useShareAnalysis';

export default function ShareAnalysisModal({ isOpen, onClose, propertyData, analysis }) {
    const { loading, error, shareUrl, createShareLink, copyToClipboard } = useShareAnalysis();
    const [expiresIn, setExpiresIn] = useState(7);
    const [copied, setCopied] = useState(false);
    const [shareData, setShareData] = useState(null);

    if (!isOpen) return null;

    const handleCreateLink = async () => {
        try {
            const result = await createShareLink(propertyData, analysis, expiresIn);
            setShareData(result);
        } catch (err) {
            // Error handled by hook
        }
    };

    const handleCopy = async () => {
        if (shareData?.shareUrl) {
            const success = await copyToClipboard(shareData.shareUrl);
            if (success) {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }
        }
    };

    const handleShare = (platform) => {
        if (!shareData?.shareUrl) return;

        const url = encodeURIComponent(shareData.shareUrl);
        const text = encodeURIComponent(`Check out this property analysis from ProprScout`);

        const shareUrls = {
            whatsapp: `https://wa.me/?text=${text}%20${url}`,
            email: `mailto:?subject=Property%20Analysis&body=${text}%20${url}`,
            twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`
        };

        if (shareUrls[platform]) {
            window.open(shareUrls[platform], '_blank', 'width=600,height=400');
        }
    };

    const expiryOptions = [
        { value: 7, label: '7 days' },
        { value: 30, label: '30 days' },
        { value: 90, label: '90 days' },
        { value: 365, label: '1 year' },
        { value: -1, label: 'Never expires' }
    ];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                            <Share2 className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Share Analysis</h2>
                            <p className="text-sm text-gray-500">Create a shareable link</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        aria-label="Close modal"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {!shareData ? (
                        <>
                            {/* Expiration Settings */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-3">
                                    <Clock className="w-4 h-4 inline mr-2" />
                                    Link Expiration
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    {expiryOptions.map((option) => (
                                        <button
                                            key={option.value}
                                            onClick={() => setExpiresIn(option.value)}
                                            className={`px-4 py-3 rounded-lg border-2 transition-all ${expiresIn === option.value
                                                ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold'
                                                : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                                }`}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Info Box */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <p className="text-sm text-blue-800">
                                    <span className="font-semibold">💡 Note:</span> Anyone with this link can view the property analysis without logging in.
                                </p>
                            </div>

                            {/* Error Display */}
                            {error && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            )}

                            {/* Create Button */}
                            <button
                                onClick={handleCreateLink}
                                disabled={loading}
                                className="w-full px-6 py-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Creating Link...
                                    </>
                                ) : (
                                    <>
                                        <Share2 className="w-5 h-5" />
                                        Create Shareable Link
                                    </>
                                )}
                            </button>
                        </>
                    ) : (
                        <>
                            {/* Success State */}
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-semibold text-emerald-900">Link Created!</p>
                                        <p className="text-sm text-emerald-700 mt-1">
                                            Share this link to give others access to the analysis.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* URL Display */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Shareable Link
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={shareData.shareUrl}
                                        readOnly
                                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                                        onClick={(e) => e.target.select()}
                                    />
                                    <button
                                        onClick={handleCopy}
                                        className={`px-4 py-3 rounded-lg font-semibold transition-all ${copied
                                            ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-500'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
                                            }`}
                                    >
                                        {copied ? (
                                            <>
                                                <Check className="w-5 h-5" />
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="w-5 h-5" />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Expiration Info */}
                            {shareData.expiresAt && shareData.expiresAt !== 'never' && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <Clock className="w-4 h-4" />
                                    <span>
                                        Expires on {new Date(shareData.expiresAt).toLocaleDateString('en-US', {
                                            month: 'long',
                                            day: 'numeric',
                                            year: 'numeric'
                                        })}
                                    </span>
                                </div>
                            )}

                            {/* Share to Platforms */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-3">
                                    Share directly to:
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        onClick={() => handleShare('whatsapp')}
                                        className="px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <MessageCircle className="w-4 h-4" />
                                        WhatsApp
                                    </button>
                                    <button
                                        onClick={() => handleShare('email')}
                                        className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <Mail className="w-4 h-4" />
                                        Email
                                    </button>
                                    <button
                                        onClick={() => handleShare('twitter')}
                                        className="px-4 py-3 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                        Twitter
                                    </button>
                                    <button
                                        onClick={() => handleShare('facebook')}
                                        className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                        Facebook
                                    </button>
                                </div>
                            </div>

                            {/* View Public Link */}
                            <a
                                href={shareData.shareUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 border border-gray-300"
                            >
                                <ExternalLink className="w-5 h-5" />
                                View Shared Page
                            </a>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
